import { Body } from "../../../components/main-body/body";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import "./admin-tools.css";

// Single discoverable landing page for diagnostic system-admin tools that
// don't belong in the regular sidebar (they're meant for occasional ops
// use, not daily workflow). The whole page and every linked tool require
// System admin — the route guard in App.js enforces it, no extra role
// filtering needed here.
//
// When you add a new tool, register it once in TOOLS and add the matching
// <Route> under <RequireSystemAdmin /> in App.js. Nothing else.
const TOOLS = [
  {
    key: "cloned-enrollment-repair",
    title: "Cloned enrollment repair",
    description:
      "LF360-1842: projects cloned before the fix kept their own default enrollment graph while their fields described the source's — the invitation form showed the wrong questions and the cohort could not be set. Lists affected projects and repairs them one transaction each, refusing any that hold work of their own unless that is confirmed.",
    path: "/administration/cloned-enrollment-repair",
  },
  {
    key: "planning-audit",
    title: "Planning audit",
    description:
      "Per-week ledger view of every confirmed subject's weekly planning run — see who's missing tasks because the cron didn't reach them, and recover them in bulk.",
    path: "/administration/planning-audit",
  },
  {
    key: "subject-planning-tools",
    title: "Subject planning tools",
    description:
      "Single-subject control panel: plan tasks (from today or week start) and adjust weekIndex as two independent actions. For ad-hoc fixes when a specific subject's schedule needs nudging.",
    path: "/administration/subject-planning-tools",
  },
  {
    key: "notification-outbox",
    title: "Notification outbox",
    description:
      "Live and historical view of the push-notification pipeline: pending, delivered, failed jobs, retry buttons, health checks. Diagnoses why a push wasn't received.",
    path: "/administration/notification-outbox",
  },
  {
    key: "project-clones",
    title: "Project clone debug",
    description:
      "Every project clone/create operation with live per-operation logs: current step, progress, heartbeat, queue state, full error details. Answers \"did my clone actually die?\" at a glance.",
    path: "/administration/project-clones",
  },
  {
    key: "article-cover-backfill",
    title: "Article cover backfill",
    description:
      "One-off migration of existing article cover images from the DB (base64) to S3. Start/restart it, watch progress (migrated/skipped/failed), and inspect per-row errors. Idempotent — safe to re-run if a pod dies mid-run.",
    path: "/administration/article-cover-backfill",
  },
  {
    key: "survey-report-backfill",
    title: "Survey report backfill",
    description:
      "Creates survey data-report rows for already-completed survey tasks that never produced them (bot / demo / admin-on-behalf completions). Start/restart, watch progress (created/skipped/failed), inspect per-row errors. Idempotent.",
    path: "/administration/survey-report-backfill",
  },
  {
    key: "task-graph-migration",
    title: "Task graph migration",
    description:
      "LF360-1815: migrates regular tasks' follow-up questions into scenario-editor graphs. Per-task journal (ok/error/skipped), rerun retries failed tasks, force re-translates untouched graphs, originals stay intact.",
    path: "/administration/task-graph-migration",
  },
];

const AdminTools = () => {
  const navigate = useProjectNavigate();

  return (
    <Body header={{ title: "Admin tools" }} scroll>
      <div className="admin-tools-page">
        <div className="admin-tools-intro">
          Diagnostic and operational tools kept out of the main sidebar to
          reduce daily noise. Bookmark this page if you find yourself coming
          back to any of them — and add new tools to this index whenever you
          ship them.
        </div>

        <div className="admin-tools-grid">
          {TOOLS.map((tool) => (
            <a
              key={tool.key}
              className="admin-tools-card"
              href={tool.path}
              onClick={(e) => {
                e.preventDefault();
                navigate(tool.path);
              }}
            >
              <div className="admin-tools-card__title">{tool.title}</div>
              <div className="admin-tools-card__desc">{tool.description}</div>
              <div className="admin-tools-card__url">{tool.path}</div>
            </a>
          ))}
        </div>
      </div>
    </Body>
  );
};

export default AdminTools;
